import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BodyOf, ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientId, PatientServicePlanId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;
type PlanStart = NonNullable<BodyOf<"put", "./service_plans/:patientServicePlanId">["planStart"]>;

/** Date inputs speak "YYYY-MM-DD"; the API speaks js-joda LocalDate. */
function toInputValue(date: LocalDate | null): string {
  return date === null ? "" : date.toString();
}

function fromInputValue(value: string): LocalDate | null {
  return value === "" ? null : LocalDate.parse(value);
}

interface Props {
  isReadOnly: boolean;
  patientId: PatientId;
  plan: ServicePlan;
  servicePlanId: PatientServicePlanId;
}

export default function ServicePlanDetailsStep({
  isReadOnly,
  patientId,
  plan,
  servicePlanId,
}: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [startDate, setStartDate] = useState(() => toInputValue(plan.startDate));
  const [endDate, setEndDate] = useState(() => toInputValue(plan.endDate));
  const [program, setProgram] = useState(plan.program ?? "");
  const [service, setService] = useState(plan.service ?? "");
  const [caseManagerName, setCaseManagerName] = useState(plan.caseManagerName ?? "");
  const [planStart, setPlanStart] = useState<PlanStart | "">(plan.planStart ?? "");
  const [visitDate, setVisitDate] = useState(() => toInputValue(plan.visitDate));
  const [visitSummary, setVisitSummary] = useState(plan.visitSummary ?? "");

  const isEndDateBeforeStartDate =
    startDate !== "" &&
    endDate !== "" &&
    LocalDate.parse(endDate).isBefore(LocalDate.parse(startDate));

  const updateMutation = useMutation({
    ...mutations.servicePlan.updateHeader(servicePlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.byId(servicePlanId).queryKey });
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.list(patientId).queryKey });
      toast({
        title: "Details saved",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not save details",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      startDate: fromInputValue(startDate),
      endDate: fromInputValue(endDate),
      program: program === "" ? null : program,
      service: service === "" ? null : service,
      caseManagerName: caseManagerName === "" ? null : caseManagerName,
      planStart: planStart === "" ? null : planStart,
      visitDate: fromInputValue(visitDate),
      visitSummary: visitSummary === "" ? null : visitSummary,
      // Owned by the tasks step, which shows it next to the hour totals.
      lrpHoursPerWeek: plan.lrpHoursPerWeek,
    });
  };

  return (
    <VStack align="stretch" spacing={5}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel>Start date</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </FormControl>

        <FormControl isInvalid={isEndDateBeforeStartDate}>
          <FormLabel>End date</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
          <FormErrorMessage>End date cannot be before the start date.</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>Program</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            placeholder="CFC"
            value={program}
            onChange={(event) => setProgram(event.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Service</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            placeholder="IHSS"
            value={service}
            onChange={(event) => setService(event.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Case manager</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            value={caseManagerName}
            onChange={(event) => setCaseManagerName(event.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Plan start</FormLabel>
          <Select
            isDisabled={isReadOnly}
            placeholder="Not specified"
            value={planStart}
            onChange={(event) => setPlanStart(event.target.value as PlanStart | "")}
          >
            <option value="NEW_CONSUMER">New consumer</option>
            <option value="RENEWING">Renewing</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Visit date</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            type="date"
            value={visitDate}
            onChange={(event) => setVisitDate(event.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel>Visit summary</FormLabel>
        <Textarea
          isReadOnly={isReadOnly}
          rows={4}
          value={visitSummary}
          onChange={(event) => setVisitSummary(event.target.value)}
        />
      </FormControl>

      {!isReadOnly && (
        <HStack justify="flex-end">
          <Button
            colorScheme="blue"
            isDisabled={isEndDateBeforeStartDate}
            isLoading={updateMutation.isPending}
            onClick={handleSave}
          >
            Save details
          </Button>
        </HStack>
      )}
    </VStack>
  );
}
